import type { MeasurementType, MeasurementTypeParameter } from '../../data/measurementTypes.ts'

export const getSensorPriority = (sensorType: string) => {
	const simplifiedDeviceModel = simplifySensorType(sensorType)
	const sensorPriorityArray = Object.keys(sensorTypeToMeasurementParameter)
	const priority = sensorPriorityArray.indexOf(simplifiedDeviceModel)
	if (priority >= 0) {
		return priority
	}
	for (const key in sensorTypeToMeasurementParameter) {
		if (simplifiedDeviceModel.startsWith(key)) {
			return sensorPriorityArray.indexOf(key)
		}
	}
	return sensorPriorityArray.length
}

const simplifySensorType = (sensorType: string) => {
	return sensorType
		.toUpperCase()
		.replace(/[^0-9a-z]/gi, '')
		.replace('SIMULATION', '')
}

/*
 * SensorType to MeasurementParameter mapping.
 * This also serves as a priority list for different sensors to determine the "primary" source
 * for a measurement or what sensor should an alert be associated with.
 */
export type PartialMeasurementType = Partial<Omit<MeasurementType, 'parameter'>> & Pick<MeasurementType, 'parameter'>
const sensorTypeToMeasurementParameter: Record<string, (MeasurementTypeParameter | PartialMeasurementType)[]> = {
	RM60: [
		'PRESENT_WEATHER',
		'PRECIPITATION_INTENSITY',
		{ parameter: 'PRECIPITATION_ACCUMULATION', period: 'PT24H' },
		{ parameter: 'PRECIPITATION_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		{ parameter: 'SNOW_ACCUMULATION', period: 'PT24H' },
		{ parameter: 'SNOW_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		'AIR_PRESSURE',
	],
	WXT530: [
		'AIR_PRESSURE',
		'AIR_TEMPERATURE',
		'RELATIVE_HUMIDITY',
		{ parameter: 'WIND_SPEED', statistic: 'MEAN' },
		{ parameter: 'WIND_SPEED', statistic: 'MAXIMUM' },
		'WIND_DIRECTION',
		'RAIN_INTENSITY',
		'RAIN_ACCUMULATION',
		'DEW_POINT_TEMPERATURE',
	],
	WXT536: [
		'AIR_PRESSURE',
		'AIR_TEMPERATURE',
		'RELATIVE_HUMIDITY',
		{ parameter: 'WIND_SPEED', statistic: 'MEAN' },
		{ parameter: 'WIND_SPEED', statistic: 'MAXIMUM' },
		'WIND_DIRECTION',
		'RAIN_INTENSITY',
		'RAIN_ACCUMULATION',
		'DEW_POINT_TEMPERATURE',
	],
	WXT535: ['AIR_PRESSURE', 'AIR_TEMPERATURE', 'RELATIVE_HUMIDITY', 'RAIN_INTENSITY', 'RAIN_ACCUMULATION', 'DEW_POINT_TEMPERATURE'],
	WXT534: ['AIR_PRESSURE', 'AIR_TEMPERATURE', 'RELATIVE_HUMIDITY', 'DEW_POINT_TEMPERATURE'],
	WXT533: [
		{ parameter: 'WIND_SPEED', statistic: 'MEAN' },
		{ parameter: 'WIND_SPEED', statistic: 'MAXIMUM' },
		'WIND_DIRECTION',
		'RAIN_INTENSITY',
		'RAIN_ACCUMULATION',
	],
	WXT532: [{ parameter: 'WIND_SPEED', statistic: 'MEAN' }, { parameter: 'WIND_SPEED', statistic: 'MAXIMUM' }, 'WIND_DIRECTION'],
	WXT531: ['RAIN_INTENSITY', 'RAIN_ACCUMULATION'],
	IOTWXT: [
		'AIR_PRESSURE',
		'AIR_TEMPERATURE',
		'DEW_POINT_TEMPERATURE',
		'FROST_POINT_TEMPERATURE',
		'RELATIVE_HUMIDITY',
		{ parameter: 'WIND_SPEED', statistic: 'MEAN', period: 'PT10M' },
		{ parameter: 'WIND_SPEED', statistic: 'MAXIMUM', period: 'PT10M' },
		'RAIN_INTENSITY',
		{ parameter: 'RAIN_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		{ parameter: 'RAIN_ACCUMULATION', period: 'PT24H' },
		{ parameter: 'RAIN_DURATION', period: 'PT24H' },
		'HAIL_INTENSITY',
		{ parameter: 'HAIL_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		{ parameter: 'HAIL_ACCUMULATION', period: 'PT24H' },
		{ parameter: 'HAIL_DURATION', period: 'PT24H' },
	],
	AQT530: [
		'NO_GAS_CONCENTRATION',
		'AIR_PRESSURE',
		'AIR_TEMPERATURE',
		'RELATIVE_HUMIDITY',
		'NO2_GAS_CONCENTRATION',
		'CO_GAS_CONCENTRATION',
		'O3_GAS_CONCENTRATION',
		'AIR_QUALITY_INDEX',
		'PARTICLE_MASS_CONCENTRATION_PM2_5',
		'PARTICLE_MASS_CONCENTRATION_PM10',
		'PARTICLE_MASS_CONCENTRATION_PM1',
		'SO2_GAS_CONCENTRATION',
		'H2S_GAS_CONCENTRATION',
	],
	AQT560: [
		'NO_GAS_CONCENTRATION',
		'AIR_PRESSURE',
		'AIR_TEMPERATURE',
		'RELATIVE_HUMIDITY',
		'NO2_GAS_CONCENTRATION',
		'CO_GAS_CONCENTRATION',
		'O3_GAS_CONCENTRATION',
		'AIR_QUALITY_INDEX',
		'PARTICLE_MASS_CONCENTRATION_PM2_5',
		'PARTICLE_MASS_CONCENTRATION_PM10',
		'PARTICLE_MASS_CONCENTRATION_PM1',
	],
	MT11: ['SURFACE_TEMPERATURE'],
	IOTHMP: ['AIR_TEMPERATURE', 'FROST_POINT_TEMPERATURE', 'DEW_POINT_TEMPERATURE', 'RELATIVE_HUMIDITY'],
	DRS611: [
		{ parameter: 'SUB_SURFACE_TEMPERATURE', height: -0.3 },
		{ parameter: 'SUB_SURFACE_TEMPERATURE', height: -0.06 },
		'SURFACE_TEMPERATURE',
		'SURFACE_STATE',
		'CHEMICAL_AMOUNT_NACL',
	],
	GMP252: ['AIR_TEMPERATURE', 'CARBON_DIOXIDE'],
	SR05D1A3: ['SHORT_WAVE_TOTAL_RADIATION'],
	SR30M2D1: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
		'TILT_ANGLE',
	],
	MS80S: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
		'TILT_ANGLE_X',
		'TILT_ANGLE_Y',
	],
	MS80SH: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
		'TILT_ANGLE_X',
		'TILT_ANGLE_Y',
	],
	MS60S: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
		'TILT_ANGLE_X',
		'TILT_ANGLE_Y',
	],
	MS60SH: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
		'TILT_ANGLE_X',
		'TILT_ANGLE_Y',
	],
	MS40S: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
		'TILT_ANGLE_X',
		'TILT_ANGLE_Y',
	],
	MS40SH: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
		'ALBEDO',
		'TILT_ANGLE',
		'TILT_ANGLE_X',
		'TILT_ANGLE_Y',
	],
	SMP10: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
	],
	SMP12: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'PLANE_OF_ARRAY_IRRADIATION',
		'REAR_PLANE_OF_ARRAY_IRRADIATION',
	],
	SPN1: [
		'GLOBAL_HORIZONTAL_IRRADIATION',
		'REFLECTED_HORIZONTAL_IRRADIATION',
		'DIFFUSE_HORIZONTAL_IRRADIATION',
		'DIRECT_NORMAL_IRRADIATION',
		'SUNSHINE_DURATION',
	],
	RG13: ['PRECIPITATION_ACCUMULATION', 'PRECIPITATION_INTENSITY'],
	RG13H: ['PRECIPITATION_ACCUMULATION', 'PRECIPITATION_INTENSITY'],
	TMPT1000: ['PV_PANEL_TEMPERATURE'],
	TMRS485MB: ['PV_PANEL_TEMPERATURE'],
	PT1000: ['PV_PANEL_TEMPERATURE'],
	PT100: ['PV_PANEL_TEMPERATURE'],
	DMA80X: ['PV_PANEL_TEMPERATURE'],
	DUSTIQ: [
		'PV_PANEL_TEMPERATURE',
		'PV_PANEL_SOILING_RATIO',
		'PV_PANEL_TRANSMISSION_LOSS',
		'PV_PANEL_SOILING_RATIO_2',
		'PV_PANEL_TRANSMISSION_LOSS_2',
	],
	MARS: ['PV_PANEL_SOILING_RATIO', 'PV_PANEL_TRANSMISSION_LOSS'],
	SNOWVU: ['SNOW_DEPTH', 'SNOW_DEPTH_TO_REF'],
	HMP155: ['AIR_TEMPERATURE', 'RELATIVE_HUMIDITY', 'DEW_POINT_TEMPERATURE'],
	DMB801: ['AIR_PRESSURE'],
	PTB330: ['AIR_PRESSURE'],
	SIRS485TCTMB: ['PV_REFERENCE_IRRADIATION'],
	WMT700: [{ parameter: 'WIND_SPEED', statistic: 'MEAN' }, { parameter: 'WIND_SPEED', statistic: 'MAXIMUM' }, 'WIND_DIRECTION'],
	DMU801: [
		// FD70
		'PRESENT_WEATHER',
		'PRECIPITATION_INTENSITY',
		{ parameter: 'PRECIPITATION_ACCUMULATION', period: 'PT24H' },
		{ parameter: 'PRECIPITATION_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		{ parameter: 'RAIN_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		{ parameter: 'HAIL_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		{ parameter: 'SNOW_ACCUMULATION', period: 'PT24H' },
		{ parameter: 'SNOW_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		{ parameter: 'VISIBILITY', period: 'PT10M' },
	],
	EGW501: [
		{ parameter: 'RAIN_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
		{ parameter: 'HAIL_ACCUMULATION', statistic: 'SUM', period: 'PT1H' },
	],
}

export const getMeasurementParametersFromSensorType = (sensorType: string): PartialMeasurementType[] | undefined => {
	const simplifiedDeviceModel = simplifySensorType(sensorType)
	let parameters = sensorTypeToMeasurementParameter[simplifiedDeviceModel]
	if (!parameters) {
		for (const key in sensorTypeToMeasurementParameter) {
			if (simplifiedDeviceModel.startsWith(key)) {
				parameters = sensorTypeToMeasurementParameter[key]
			}
		}
	}
	if (!parameters) {
		return undefined
	}
	return parameters.map((parameter) => {
		if (typeof parameter === 'string') {
			return {
				parameter: parameter,
			}
		}
		return parameter
	})
}
